/**
 * Kit de soporte v1 — contexto que un reporte lleva solo (ADR-0025).
 *
 * Dos buffers chicos se llenan mientras la persona trabaja: los últimos errores
 * del navegador (solo el mensaje, sin stack) y las últimas llamadas fallidas a
 * la API con su `X-Request-ID`, para que el ticket apunte directo a las líneas
 * de log. Las rutas van como plantilla (`/api/v1/runs/:id`) y sin query: los
 * ids y las búsquedas no llegan al ticket. La identidad nunca sale de aquí; el
 * backend la toma del token.
 *
 * La app llama `installErrorCapture()` al arrancar y `recordApiError()` desde
 * su cliente de API cuando una llamada falla.
 */

const MAX_ITEMS = 5;
const MAX_LEN = 200;

export interface ApiErrorEntry {
  method: string;
  path: string;
  status: number;
  code: string | null;
  request_id: string | null;
}

const jsErrors: string[] = [];
const apiErrors: ApiErrorEntry[] = [];
let installed = false;

function push<T>(buffer: T[], item: T): void {
  buffer.push(item);
  if (buffer.length > MAX_ITEMS) buffer.shift();
}

// Un segmento que parece id (UUID, hex largo o una corrida larga de dígitos).
const ID_SEGMENT =
  /\/(?:[0-9a-fA-F]{8}-[0-9a-fA-F-]{27,}|[0-9a-fA-F]{16,}|\d{6,})(?=\/|$)/g;

export function templatePath(path: string): string {
  let bare = path.split("?")[0]?.split("#")[0] ?? "";
  if (/^https?:\/\//i.test(bare)) {
    try {
      bare = new URL(bare).pathname;
    } catch {
      bare = "/";
    }
  }
  const templated = bare.replace(ID_SEGMENT, "/:id");
  return (templated.startsWith("/") ? templated : `/${templated}`).slice(
    0,
    MAX_LEN,
  );
}

export function recordJsError(message: unknown): void {
  const text = String(message ?? "").trim();
  if (text) push(jsErrors, text.slice(0, MAX_LEN));
}

const REQUEST_ID = /^[A-Za-z0-9-]{8,64}$/;
const METHOD = /^[A-Z]{3,7}$/;

export function recordApiError(entry: {
  method: string;
  path: string;
  status: number;
  code?: string | null;
  requestId?: string | null;
}): void {
  const method = entry.method.toUpperCase();
  push(apiErrors, {
    method: METHOD.test(method) ? method : "GET",
    path: templatePath(entry.path),
    status: Math.max(0, Math.min(599, entry.status)),
    code: entry.code ? entry.code.slice(0, 64) : null,
    request_id:
      entry.requestId && REQUEST_ID.test(entry.requestId)
        ? entry.requestId
        : null,
  });
}

/** Instala una sola vez los listeners globales de errores (al arrancar). */
export function installErrorCapture(target: Window = window): void {
  if (installed) return;
  installed = true;
  target.addEventListener("error", (event) => recordJsError(event.message));
  target.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { message?: unknown } | undefined;
    recordJsError(reason?.message ?? event.reason);
  });
}

export function newReportId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Respaldo RFC 4122 v4 para navegadores sin randomUUID.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface ReportContext {
  ruta: string;
  app_version: string | null;
  viewport: string | null;
  idioma: string | null;
  zona_horaria: string | null;
  errores_js: string[];
  api_errors: ApiErrorEntry[];
}

const APP_VERSION = /^[\w.-]{1,64}$/;

export function buildReportContext(pathname: string): ReportContext {
  const version: unknown = import.meta.env.VITE_APP_VERSION;
  let zone: string | null;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    zone = null;
  }
  return {
    ruta: templatePath(pathname || "/"),
    app_version:
      typeof version === "string" && APP_VERSION.test(version)
        ? version.slice(0, 12)
        : null,
    viewport:
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}`
        : null,
    idioma:
      typeof navigator !== "undefined" ? navigator.language.slice(0, 35) : null,
    zona_horaria: zone ? zone.slice(0, 64) : null,
    errores_js: [...jsErrors],
    api_errors: [...apiErrors],
  };
}

/** Para pruebas: vacía los buffers entre casos. */
export function resetSupportContext(): void {
  jsErrors.length = 0;
  apiErrors.length = 0;
}
